import LogoSVG from '@assets/player/logo-shadow.svg';
import classNames from 'classnames';
import { type FC, useEffect, useRef, useState } from 'react';
import { sendAnalyticsEvent } from 'src/lib/googleAnalytics';
import ControlBar from '../components/Player/ControlBar';
import InfoSheet, { useAvailableInfoSheetTabs } from '../components/Player/InfoSheet';
import MapView from '../components/Player/Map';
import { PlayerSideBar, SIDEBAR_WIDTH_BREAKPOINT } from '../components/Player/PlayerSideBar';
import { SideBarButton } from '../components/Player/SideBar';
import { SponsorLogos } from '../components/Player/SponsorLogos/SponsorLogos';
import { ClockOffsetWarning } from '../lib/player/controller/ClockOffsetWarning';
import { EventReloadManager } from '../lib/player/controller/EventReloadManager';
import { HashManager } from '../lib/player/controller/HashManager';
import { KeyPressManager } from '../lib/player/controller/KeyPressManager';
import { GroupLiveMotionManager } from '../lib/player/controller/LiveMotionManager';
import { OwnLocationManager } from '../lib/player/controller/OwnLocationManager';
import { GroupStarterTrackManager } from '../lib/player/controller/StarterTrackManager';
import { Timer } from '../lib/player/controller/Timer';
import { triggerMapLoad } from '../lib/player/model';
import { getCurrentEvents, reduceStartTime } from '../lib/player/selectors';
import { useStore } from '../lib/player/store';
import { correctedNow } from '../lib/player/utils/clock';
import { useMeasure } from '../lib/player/utils/hooks';

export const PlayerView: FC = () => {
  const [visibleMapRef, visibleMapBounds] = useMeasure({
    debounce: 100,
    scroll: false,
  });
  const [mapContainerRef, mapContainerBounds] = useMeasure({
    debounce: 100,
    scroll: false,
  });
  const [mapViewIsInitialized, setMapViewIsInitialized] = useState(false);

  const isPlaying = useStore((state) => state.timer.isPlaying);
  const currentEvents = useStore((state) => getCurrentEvents(state));
  const currentEvent1 = currentEvents.at(0);
  const startTime = reduceStartTime(currentEvents);
  const eventId = useStore((state) => state.eventId);

  const shouldShowInfoSheet =
    currentEvent1 != null && Object.values(useAvailableInfoSheetTabs(currentEvent1)).some((a) => a);
  const [showSideBar, setShowSideBar] = useState(
    window.innerWidth > SIDEBAR_WIDTH_BREAKPOINT && !!currentEvent1?.options.listOpenByDefault,
  );

  const pageviewTrackerRef = useRef<Set<string> | null>(null);

  function handleRacemapLogoClick() {
    sendAnalyticsEvent('racemap-logo-click');
  }

  useEffect(() => {
    let pageViewTracker = pageviewTrackerRef.current;
    if (pageViewTracker == null) {
      pageviewTrackerRef.current = new Set();
      pageViewTracker = pageviewTrackerRef.current;
    }
    if (!pageViewTracker.has(eventId)) {
      triggerMapLoad(eventId);
      pageViewTracker.add(eventId);
    }
  }, [eventId]);

  useEffect(() => {
    if (
      visibleMapBounds.width > 0 &&
      mapContainerBounds.height > 0 &&
      (visibleMapBounds.height !== mapContainerBounds.height || !shouldShowInfoSheet)
    ) {
      setMapViewIsInitialized(true);
    }
  }, [visibleMapBounds, mapContainerBounds, shouldShowInfoSheet]);

  console.debug(
    visibleMapBounds.width,
    visibleMapBounds.height,
    mapContainerBounds.width,
    mapContainerBounds.height,
    shouldShowInfoSheet,
    visibleMapBounds.width > 0 &&
      mapContainerBounds.height > 0 &&
      (visibleMapBounds.height !== mapContainerBounds.height || !shouldShowInfoSheet),
  );

  return (
    <div className={classNames('inner', { 'side-bar-open': showSideBar })}>
      <ClockOffsetWarning />
      {isPlaying && <Timer interval={50} />}
      {startTime <= correctedNow() && <KeyPressManager />}
      <GroupLiveMotionManager />
      <GroupStarterTrackManager />
      <HashManager />
      <OwnLocationManager />
      <EventReloadManager />
      <div className="map-container">
        {mapContainerBounds.height > 0 && (
          <PlayerSideBar
            closed={!showSideBar}
            onClose={() => {
              setShowSideBar(false);
            }}
          />
        )}
        <div className="map-container-sizer" ref={mapContainerRef}>
          {visibleMapBounds.width > 0 && mapContainerBounds.height > 0 && mapViewIsInitialized && (
            <MapView mapBounds={mapContainerBounds} visibleMapBounds={visibleMapBounds} />
          )}

          {mapContainerBounds.height > 0 && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div
                style={{ flex: 1, position: 'relative', pointerEvents: 'none' }}
                ref={visibleMapRef}
              >
                {visibleMapBounds.width > 0 && (
                  <>
                    <div className="logos" style={{ pointerEvents: 'all' }}>
                      {/* biome-ignore lint/a11y/useAnchorContent: mapbox inject that info */}
                      <a
                        href="https://mapbox.com/"
                        className="mapboxgl-ctrl-logo"
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                      <a
                        href="https://racemap.com/"
                        title="» Racemap"
                        className="link-button"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleRacemapLogoClick}
                      >
                        <img src={LogoSVG} alt="» Racemap" />
                      </a>
                      <SponsorLogos />
                    </div>
                    <SideBarButton
                      isOpen={showSideBar}
                      onToggle={() => {
                        setShowSideBar((v) => !v);
                      }}
                    />
                  </>
                )}
              </div>
              {shouldShowInfoSheet && mapContainerBounds.height > 0 && (
                <InfoSheet
                  maxHeight={mapContainerBounds.height}
                  isInitiallyOpen={currentEvent1.options.infoTabs.openByDefault}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <ControlBar className="control-bar" />
    </div>
  );
};
